import { useQuery } from '@tanstack/react-query';

import { documentsEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import { DatasourceDocument } from '@/modules/collections/types';
import { Pagination } from '@/shared/types/common';

import { queryKeyFactories } from '../../../hooks/utils/queryKeyFactory';

/**
 * Type for the API response that contains draft documents and pagination
 */
interface DraftDocumentsResponse {
  documents: DatasourceDocument[];
  pagination: Pagination['pagination'];
}

/**
 * Hook parameters interface
 */
interface UseAPIDraftDocumentsCountParams {
  collection_id?: string;
  enabled?: boolean;
}

/**
 * API methods for the hook
 */
const API = Object.freeze({
  /**
   * Fetch draft documents count for a collection
   * Only fetches pagination data with limit=1 for performance
   */
  fetchDraftDocumentsCount: async (collection_id: string): Promise<DraftDocumentsResponse> => {
    return callGet(documentsEndpoints.draftDocumentsCount(collection_id));
  },
});

/**
 * Custom React hook for getting the count of draft documents in a collection
 *
 * This hook provides a lightweight way to fetch only the count of draft documents
 * without fetching all the document data. It uses limit=1 on the API call and
 * extracts the total count from the pagination object.
 *
 * @param collection_id - The collection identifier
 * @param enabled - Whether the query should be enabled (defaults to true)
 *
 * @returns Object containing:
 *  - count: Number of draft documents in the collection
 *  - isLoading: Loading state
 *  - error: Error object if the query failed
 *  - refetch: Function to manually refetch the count
 *
 * @example
 * ```tsx
 * const { count, isLoading } = useAPIDraftDocumentsCount({
 *   collection_id: 'abc-123',
 *   enabled: true
 * });
 *
 * return <div>Draft documents: {count}</div>;
 * ```
 */
export const useAPIDraftDocumentsCount = ({
  collection_id,
  enabled = true,
}: UseAPIDraftDocumentsCountParams) => {
  // Generate query key
  const queryKey = queryKeyFactories.draftDocumentsCount(collection_id);

  // Fetch data
  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery<DraftDocumentsResponse, Error>({
    queryKey,
    queryFn: () => API.fetchDraftDocumentsCount(collection_id),
    enabled: !!collection_id && enabled,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return { count: response?.pagination?.total || 0, isLoading, error, refetch };
};
